import { useTenant } from '$features/auth/hooks/use-tenant'
import { DateHelper } from '$utils/date'
import { ScheduledPerson } from './use-calendar-slots'
import { Dayjs } from 'dayjs'

const DEFAULT_OPEN_TIME = '09:00'
const DEFAULT_CLOSE_TIME = '17:00'

export function useCalendarSettings() {
  const { businessHours } = useTenant()

  const getDayServiceHours = (dayOfWeek: number, date?: Dayjs) => {
    return businessHours[dayOfWeek] ?? {
      open: DateHelper.timeToDate(DEFAULT_OPEN_TIME, date),
      close: DateHelper.timeToDate(DEFAULT_CLOSE_TIME, date),
    }
  }

  const getPersonWorkingWindow = (dayOfWeek: number, person?: ScheduledPerson, date?: Dayjs) => {
    const { open, close } = getDayServiceHours(dayOfWeek, date)

    if (!person) {
      return [{
        start: DateHelper.timeToDate(open, date),
        finish: DateHelper.timeToDate(close, date),
      }]
    }

    const schedule = person.schedule.find(schedule => Number(schedule.day) === dayOfWeek)

    if (!schedule || !schedule.times || schedule.times.length === 0) {
      return [{
        start: DateHelper.timeToDate(open, date),
        finish: DateHelper.timeToDate(close, date),
      }]
    }

    return schedule.times.map(timeSlot => ({
      start: DateHelper.timeToDate(timeSlot.from, date),
      finish: DateHelper.timeToDate(timeSlot.to, date),
    }))
  }

  const getHighestWorkingWindow = (people: ScheduledPerson[], date?: Dayjs) => {
    const workingWindows = people.flatMap(person => person.schedule.map(schedule => {
      const dayOfWeek = Number(schedule.day)
      const personTimes = getPersonWorkingWindow(dayOfWeek, person, date)

      if (personTimes.length === 0) {
        return {
          start: DateHelper.timeToDate(DEFAULT_OPEN_TIME, date),
          finish: DateHelper.timeToDate(DEFAULT_CLOSE_TIME, date),
        }
      }

      const earliestStart = personTimes.reduce((earliest, current) => current.start.isBefore(earliest.start) ? current : earliest, personTimes[0])
      const latestFinish = personTimes.reduce((latest, current) => current.finish.isAfter(latest.finish) ? current : latest, personTimes[0])

      return { start: earliestStart.start, finish: latestFinish.finish }
    }))

    return workingWindows.reduce((max, current) => {
      return current?.finish.isAfter(max?.finish) ? current : max
    }, {
      start: DateHelper.timeToDate(DEFAULT_OPEN_TIME, date),
      finish: DateHelper.timeToDate(DEFAULT_CLOSE_TIME, date),
    })
  }

  const getSlotWindow = (people: ScheduledPerson[], date?: Dayjs) => {
    let open = DateHelper.timeToDate(DEFAULT_OPEN_TIME, date)
    let close = DateHelper.timeToDate(DEFAULT_CLOSE_TIME, date)

    const highestWorkingWindow = getHighestWorkingWindow(people, date)

    for (const serviceHour of businessHours) {
      const openTime = DateHelper.timeToDate(serviceHour.open, date)
      const closeTime = DateHelper.timeToDate(serviceHour.close, date)

      if (openTime.isBefore(open)) {
        open = openTime
      }

      if (closeTime.isAfter(close)) {
        close = closeTime
      }

      if (highestWorkingWindow.start.isBefore(open)) {
        open = highestWorkingWindow.start
      }

      if (highestWorkingWindow.finish.isAfter(close)) {
        close = highestWorkingWindow.finish
      }
    }

    return {
      start: open,
      finish: close,
    }
  }

  return {
    getDayServiceHours,
    getSlotWindow,
    getPersonWorkingWindow,
  }
}
